/*
    The purpose of this code is to create and manage a canvas for rendering avatars in a web application.
    It initializes the canvas, sets up the rendering context, and provides methods to draw avatars on the canvas.
*/

import type { BannerSlices, Padding, Point, Rect } from "./Types";

export class AvatarCanvas {
    private _canvas: HTMLCanvasElement;
    private _context: CanvasRenderingContext2D | null;

    constructor(canvas: HTMLCanvasElement) {
        this._canvas = canvas;
        this._canvas.style.width = "100%";
        this._canvas.style.height = "100%";
        this._canvas.style.imageRendering = "pixelated";

        this._canvas.width = 1920;
        this._canvas.height = 1080;

        this._context = this._canvas.getContext("2d");
        if (this._context) this._context.imageSmoothingEnabled = false;
    }

    get canvas() {
        return this._canvas;
    }
    get width() {
        return this._canvas.width;
    }
    get height() {
        return this._canvas.height;
    }
    set width(value) {
        this._canvas.width = value;
    }
    set height(value) {
        this._canvas.height = value;
    }

    get fillStyle(): string | CanvasGradient | CanvasPattern | null {
        return this._context?.fillStyle ?? null;
    }
    set fillStyle(value: string | CanvasGradient | CanvasPattern) {
        if (this._context) this._context.fillStyle = value;
    }
    get strokeStyle(): string | CanvasGradient | CanvasPattern | null {
        return this._context?.strokeStyle ?? null;
    }
    set strokeStyle(value: string | CanvasGradient | CanvasPattern) {
        if (this._context) this._context.strokeStyle = value;
    }

    get font(): string | null {
        return this._context?.font ?? null;
    }
    set font(value: string) {
        if (this._context) this._context.font = value;
    }

    get textAlign(): CanvasTextAlign | null {
        return this._context?.textAlign ?? null;
    }
    set textAlign(value: CanvasTextAlign) {
        if (this._context) this._context.textAlign = value;
    }

    get textBaseline(): CanvasTextBaseline | null {
        return this._context?.textBaseline ?? null;
    }
    set textBaseline(value: CanvasTextBaseline) {
        if (this._context) this._context.textBaseline = value;
    }

    get globalAlpha(): number | null {
        return this._context?.globalAlpha ?? null;
    }
    set globalAlpha(value: number) {
        if (this._context) this._context.globalAlpha = value;
    }

    get lineWidth(): number | null {
        return this._context?.lineWidth ?? null;
    }
    set lineWidth(value: number) {
        if (this._context) this._context.lineWidth = value;
    }

    get lineCap(): CanvasLineCap | null {
        return this._context?.lineCap ?? null;
    }
    set lineCap(value: CanvasLineCap) {
        if (this._context) this._context.lineCap = value;
    }

    get lineJoin(): CanvasLineJoin | null {
        return this._context?.lineJoin ?? null;
    }
    set lineJoin(value: CanvasLineJoin) {
        if (this._context) this._context.lineJoin = value;
    }

    get miterLimit(): number | null {
        return this._context?.miterLimit ?? null;
    }
    set miterLimit(value: number) {
        if (this._context) this._context.miterLimit = value;
    }

    get shadowColor(): string | null {
        return this._context?.shadowColor ?? null;
    }
    set shadowColor(value: string) {
        if (this._context) if (this._context) this._context.shadowColor = value;
    }

    get shadowBlur(): number | null {
        return this._context?.shadowBlur ?? null;
    }
    set shadowBlur(value: number) {
        if (this._context) this._context.shadowBlur = value;
    }

    get shadowOffsetX(): number | null {
        return this._context?.shadowOffsetX ?? null;
    }
    set shadowOffsetX(value: number) {
        if (this._context) this._context.shadowOffsetX = value;
    }

    get shadowOffsetY(): number | null {
        return this._context?.shadowOffsetY ?? null;
    }
    set shadowOffsetY(value: number) {
        if (this._context) this._context.shadowOffsetY = value;
    }

    clear() {
        this._context?.clearRect(0, 0, this._canvas.width, this._canvas.height);
    }

    validateSize() {
        const rect = this._canvas.getBoundingClientRect();
        const width = Math.floor(rect.width);
        const height = Math.floor(rect.height);

        // Only resize if needed
        if (this._canvas.width !== width || this.canvas.height !== height) {
            this._canvas.width = width;
            this._canvas.height = height;
        }
        if (this._context) this._context.imageSmoothingEnabled = false;
    }

    // Internal helper to flip Y coordinate with optional height (for shapes)
    _flipY(y: number, height = 0) {
        return this._canvas.height - y - height;
    }

    // Convert screen coords (e.g. mouse) to bottom-left Y-up coords
    toCartesianCoords(screenPoint: Point) {
        return {
            x: screenPoint.x,
            y: this._canvas.height - screenPoint.y,
        };
    }

    // === Drawing wrappers ===

    drawImage(image: HTMLImageElement, rect: Rect) {
        this._context?.drawImage(
            image,
            rect.x,
            this._flipY(rect.y, rect.height),
            rect.width,
            rect.height
        );
    }

    // Draw a frame from a sprite sheet
    // sx, sy: source coordinates in the sprite sheet
    // sWidth, sHeight: source dimensions in the sprite sheet
    // scale: scale factor for the drawn frame
    // dx, dy: destination coordinates on the canvas
    drawFrame(
        image: HTMLImageElement,
        frame: Rect,
        location: Point,
        scale = 1
    ) {
        this._context?.drawImage(
            image,
            frame.x,
            frame.y,
            frame.width,
            frame.height,
            location.x,
            this._flipY(location.y, frame.height * scale),
            frame.width * scale,
            frame.height * scale
        );
    }

    drawBanner(
        image: HTMLImageElement,
        frame: Rect,
        location: Point,
        targetWidth: number,
        slices: BannerSlices,
        padding: Padding,
        scale = 1
    ) {
        if (!this._context) return;

        const ctx = this._context;

        const leftEdgeWidth = slices.left;
        const rightEdgeWidth = slices.right;

        const sourceCenterWidth = image.width - leftEdgeWidth - rightEdgeWidth;

        const leftDestEdgeWidth = leftEdgeWidth * scale;
        const rightDestEdgeWidth = rightEdgeWidth * scale;
        const destCenterWidth = Math.ceil(
            targetWidth -
                leftEdgeWidth -
                rightEdgeWidth +
                padding.left +
                padding.right
        );

        const destHeight = Math.ceil(frame.height * scale);
        const destY = this._flipY(location.y - 5, destHeight);

        location.x -= Math.ceil(targetWidth / 2 + padding.left);

        // Left cap
        ctx.drawImage(
            image,
            frame.x,
            frame.y,
            leftEdgeWidth,
            frame.height,
            location.x,
            destY,
            leftDestEdgeWidth,
            destHeight
        );

        // Center (stretched horizontally)
        ctx.drawImage(
            image,
            frame.x + leftEdgeWidth,
            frame.y,
            sourceCenterWidth,
            frame.height,
            location.x + leftDestEdgeWidth,
            destY,
            destCenterWidth,
            destHeight
        );

        // Right cap
        ctx.drawImage(
            image,
            frame.x + image.width - rightEdgeWidth,
            frame.y,
            rightDestEdgeWidth,
            frame.height,
            location.x + leftDestEdgeWidth + destCenterWidth,
            destY,
            rightDestEdgeWidth,
            destHeight
        );
    }

    moveTo(location: Point) {
        this._context?.moveTo(location.x, this._flipY(location.y));
    }

    lineTo(location: Point) {
        this._context?.lineTo(location.x, this._flipY(location.y));
    }

    arc(
        location: Point,
        radius: number,
        startAngle: number,
        endAngle: number,
        anticlockwise = false
    ) {
        this._context?.arc(
            location.x,
            this._flipY(location.y),
            radius,
            startAngle,
            endAngle,
            anticlockwise
        );
    }

    ellipse(
        location: Point,
        radius: Point,
        rotation: number,
        startAngle: number,
        endAngle: number,
        anticlockwise = false
    ) {
        this._context?.ellipse(
            location.x,
            this._flipY(location.y),
            radius.x,
            radius.y,
            rotation,
            startAngle,
            endAngle,
            anticlockwise
        );
    }

    quadraticCurveTo(control: Point, end: Point) {
        this._context?.quadraticCurveTo(
            control.x,
            this._flipY(control.y),
            end.x,
            this._flipY(end.y)
        );
    }

    bezierCurveTo(cp1: Point, cp2: Point, end: Point) {
        this._context?.bezierCurveTo(
            cp1.x,
            this._flipY(cp1.y),
            cp2.x,
            this._flipY(cp2.y),
            end.x,
            this._flipY(end.y)
        );
    }

    arcTo(start: Point, end: Point, radius: number) {
        this._context?.arcTo(
            start.x,
            this._flipY(start.y),
            end.x,
            this._flipY(end.y),
            radius
        );
    }

    fillText(
        text: string,
        location: Point,
        maxWidth: number | null = null,
        centered = false
    ) {
        if (!this._context) return;

        if (centered) {
            // Center the text horizontally
            const textMetrics = this._context.measureText(text);
            location.x -= textMetrics.width / 2;
        }

        const flippedY = this._flipY(location.y);
        if (maxWidth) {
            this._context.fillText(text, location.x, flippedY, maxWidth);
        } else {
            this._context.fillText(text, location.x, flippedY);
        }
    }

    strokeText(text: string, location: Point, maxWidth: number | null) {
        if (!this._context) return;

        const flippedY = this._flipY(location.y);
        if (maxWidth) {
            this._context.strokeText(text, location.x, flippedY, maxWidth);
        } else {
            this._context.strokeText(text, location.x, flippedY);
        }
    }

    fillRect(rect: Rect) {
        this._context?.fillRect(
            rect.x,
            this._flipY(rect.y, rect.height),
            rect.width,
            rect.height
        );
    }

    fillRoundedRect(rect: Rect, radius: number, centered = false) {
        if (centered) {
            rect.x -= rect.width / 2;
        }

        const flippedY = this._flipY(rect.y, rect.height);

        this._context?.beginPath();
        this._context?.moveTo(rect.x + radius, flippedY);
        this._context?.lineTo(rect.x + rect.width - radius, flippedY);
        this._context?.arc(
            rect.x + rect.width - radius,
            flippedY + radius,
            radius,
            -Math.PI / 2,
            0,
            false
        );
        this._context?.lineTo(
            rect.x + rect.width,
            flippedY + rect.height - radius
        );
        this._context?.arc(
            rect.x + rect.width - radius,
            flippedY + rect.height - radius,
            radius,
            0,
            Math.PI / 2,
            false
        );
        this._context?.lineTo(rect.x + radius, flippedY + rect.height);
        this._context?.arc(
            rect.x + radius,
            flippedY + rect.height - radius,
            radius,
            Math.PI / 2,
            Math.PI,
            false
        );
        this._context?.lineTo(rect.x, flippedY + radius);
        this._context?.arc(
            rect.x + radius,
            flippedY + radius,
            radius,
            Math.PI,
            1.5 * Math.PI,
            false
        );
        this._context?.closePath();
        this._context?.fill();
    }

    strokeRect(rect: Rect) {
        this._context?.strokeRect(
            rect.x,
            this._flipY(rect.y, rect.height),
            rect.width,
            rect.height
        );
    }

    clearRect(rect: Rect) {
        this._context?.clearRect(
            rect.x,
            this._flipY(rect.y, rect.height),
            rect.width,
            rect.height
        );
    }

    // === Path methods ===

    beginPath() {
        this._context?.beginPath();
    }

    closePath() {
        this._context?.closePath();
    }

    clip() {
        this._context?.clip();
    }

    // === State save/restore ===

    save() {
        this._context?.save();
    }

    restore() {
        this._context?.restore();
    }

    // === Transform ===

    setTransform(
        a: number,
        b: number,
        c: number,
        d: number,
        e: number,
        f: number
    ) {
        this._context?.setTransform(a, b, c, d, e, f);
    }

    resetTransform() {
        this._context?.resetTransform();
    }

    // === Gradients ===

    createLinearGradient(start: Point, end: Point) {
        return this._context?.createLinearGradient(
            start.x,
            this._flipY(start.y),
            end.x,
            this._flipY(end.y)
        );
    }

    createRadialGradient(
        start: Point,
        radius0: number,
        end: Point,
        radius1: number
    ) {
        return this._context?.createRadialGradient(
            start.x,
            this._flipY(start.y),
            radius0,
            end.x,
            this._flipY(end.y),
            radius1
        );
    }

    // === Text measurement ===

    measureText(text: string) {
        return this._context?.measureText(text);
    }
}
